import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, Award, TrendingUp, BookOpen, Target, Lightbulb, FileCheck, Plus, X, FileText, Download, AlertCircle, CheckCircle, Info, School, MapPin, ExternalLink } from 'lucide-react';
import { analyzeMarksheetPDFs } from '../../services/pdfAnalysisService';
import { admissionsService } from '../../firebase/admissions';

const ProfileIntegrationPanel = ({ cognitiveProfile, behavioralProfile, profileSettings }) => {
  const [interests, setInterests] = useState(profileSettings?.profile?.interests || []);
  const [showInterestInput, setShowInterestInput] = useState(false);
  const [newInterest, setNewInterest] = useState('');
  const [pdfAnalysis, setPdfAnalysis] = useState(null);
  const [loadingPdfAnalysis, setLoadingPdfAnalysis] = useState(false);
  const [suggestedSchools, setSuggestedSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [userClass, setUserClass] = useState(null);

  // Analyze PDFs when component mounts or profileSettings changes
  useEffect(() => {
    const analyzePDFs = async () => {
      if (profileSettings?.profile?.results) {
        setLoadingPdfAnalysis(true);
        try {
          const analysis = await analyzeMarksheetPDFs(profileSettings);
          setPdfAnalysis(analysis);
        } catch (error) {
          console.error('Error analyzing PDFs:', error);
        } finally {
          setLoadingPdfAnalysis(false);
        }
      }
    };

    analyzePDFs();
  }, [profileSettings]);

  // Calculate school score based on multiple factors
  const calculateSchoolScore = (school) => {
    let score = 0;
    const breakdown = {
      experience: 0,
      faculty: 0,
      courses: 0,
      results: 0,
      facilities: 0,
      affordability: 0,
      total: 0
    };

    // Experience Score (max 20 points)
    if (school.established) {
      const currentYear = new Date().getFullYear();
      const establishedYear = parseInt(school.established);
      const yearsOfExperience = currentYear - establishedYear;
      if (yearsOfExperience >= 50) breakdown.experience = 20;
      else if (yearsOfExperience >= 30) breakdown.experience = 18;
      else if (yearsOfExperience >= 20) breakdown.experience = 15;
      else if (yearsOfExperience >= 10) breakdown.experience = 12;
      else if (yearsOfExperience >= 5) breakdown.experience = 8;
      else breakdown.experience = 5;
    }

    // Faculty Score (max 20 points)
    const facultyCount = school.faculty?.length || 0;
    if (facultyCount >= 50) breakdown.faculty = 20;
    else if (facultyCount >= 30) breakdown.faculty = 18;
    else if (facultyCount >= 20) breakdown.faculty = 15;
    else if (facultyCount >= 10) breakdown.faculty = 12;
    else if (facultyCount >= 5) breakdown.faculty = 8;
    else if (facultyCount > 0) breakdown.faculty = 5;

    // Courses Score (max 20 points)
    const coursesCount = school.curriculum?.length || 0;
    const programsCount = school.programs?.length || 0;
    const totalOfferings = coursesCount + programsCount;
    if (totalOfferings >= 20) breakdown.courses = 20;
    else if (totalOfferings >= 15) breakdown.courses = 18;
    else if (totalOfferings >= 10) breakdown.courses = 15;
    else if (totalOfferings >= 5) breakdown.courses = 12;
    else if (totalOfferings > 0) breakdown.courses = 8;

    // Results Score (max 20 points)
    const resultsCount = school.results?.length || 0;
    const totalQualified = school.results?.reduce((sum, r) => sum + (parseInt(r.qualified) || 0), 0) || 0;
    if (resultsCount >= 5 && totalQualified >= 100) breakdown.results = 20;
    else if (resultsCount >= 3 && totalQualified >= 50) breakdown.results = 18;
    else if (resultsCount >= 2 && totalQualified >= 20) breakdown.results = 15;
    else if (resultsCount >= 1 && totalQualified >= 10) breakdown.results = 12;
    else if (resultsCount > 0) breakdown.results = 8;

    // Facilities Score (max 10 points)
    const featuresCount = school.features?.length || 0;
    const materialsCount = school.materials?.length || 0;
    const hasVirtualTour = school.virtualTourVideo ? 3 : 0;
    breakdown.facilities = Math.min(10, Math.floor(featuresCount / 2) + Math.floor(materialsCount / 2) + hasVirtualTour);

    // Affordability Score (max 10 points) - Lower fee is better
    const appFee = parseInt(school.applicationFee) || 0;
    if (appFee === 0) breakdown.affordability = 10;
    else if (appFee <= 500) breakdown.affordability = 9;
    else if (appFee <= 1000) breakdown.affordability = 8;
    else if (appFee <= 2000) breakdown.affordability = 6;
    else if (appFee <= 5000) breakdown.affordability = 4;
    else breakdown.affordability = 2;

    breakdown.total = breakdown.experience + breakdown.faculty + breakdown.courses + 
                     breakdown.results + breakdown.facilities + breakdown.affordability;

    return breakdown;
  };

  // Extract city from student's address
  const extractCityFromAddress = (address) => {
    if (!address) return null;
    
    // Try to extract city from address string
    // Common patterns: "Street, City, State" or "City, State" or just "City"
    const parts = address.split(',').map(p => p.trim());
    
    // If multiple parts, assume city is the second-to-last or last meaningful part
    if (parts.length >= 2) {
      // Return the part that's likely the city (usually before state/pincode)
      return parts[parts.length - 2] || parts[parts.length - 1];
    } else if (parts.length === 1) {
      // Single part - could be just city
      return parts[0];
    }
    
    return null;
  };

  // Detect user's class from interests and fetch school suggestions
  useEffect(() => {
    const detectClassAndFetchSchools = async () => {
      // Check if user has mentioned their class in interests
      const classPattern = /class\s*(\d{1,2})|(\d{1,2})(?:th|st|nd|rd)?\s*class|grade\s*(\d{1,2})/i;
      let detectedClass = null;

      for (const interest of interests) {
        const match = interest.match(classPattern);
        if (match) {
          detectedClass = parseInt(match[1] || match[2] || match[3]);
          if (detectedClass >= 1 && detectedClass <= 12) {
            break;
          }
        }
      }

      if (detectedClass && detectedClass !== userClass) {
        setUserClass(detectedClass);
        setLoadingSchools(true);
        try {
          // Get student's city from profile
          const studentAddress = profileSettings?.profile?.personalInfo?.address;
          const studentCity = extractCityFromAddress(studentAddress);
          
          console.log('Student address:', studentAddress);
          console.log('Extracted city:', studentCity);

          // Fetch schools from database
          const result = await admissionsService.getAllInstitutions({
            type: 'school',
            isActive: true
          });

          if (result.success && result.institutions) {
            let schools = result.institutions;

            // Filter by city if student city is available
            if (studentCity) {
              const cityLower = studentCity.toLowerCase();
              const localSchools = schools.filter(school => {
                const schoolCity = school.address?.city?.toLowerCase();
                const schoolState = school.address?.state?.toLowerCase();
                const schoolAddress = `${school.address?.street || ''} ${school.address?.city || ''} ${school.address?.state || ''}`.toLowerCase();
                
                // Match if city is in school's city, state, or full address
                return schoolCity?.includes(cityLower) || 
                       cityLower.includes(schoolCity) ||
                       schoolAddress.includes(cityLower);
              });

              // If we found local schools, prioritize them; otherwise use all schools
              if (localSchools.length > 0) {
                schools = localSchools;
                console.log(`Found ${localSchools.length} schools in ${studentCity}`);
              } else {
                console.log(`No schools found in ${studentCity}, showing all schools`);
              }
            }

            // Calculate scores for all schools
            const schoolsWithScores = schools.map(school => ({
              ...school,
              scoreBreakdown: calculateSchoolScore(school)
            }));

            // Sort by total score (highest first)
            schoolsWithScores.sort((a, b) => b.scoreBreakdown.total - a.scoreBreakdown.total);

            // Take top 10 schools
            setSuggestedSchools(schoolsWithScores.slice(0, 10));
          }
        } catch (error) {
          console.error('Error fetching schools:', error);
        } finally {
          setLoadingSchools(false);
        }
      } else if (!detectedClass && userClass) {
        // User removed class from interests
        setUserClass(null);
        setSuggestedSchools([]);
      }
    };

    detectClassAndFetchSchools();
  }, [interests, userClass, profileSettings]);

  if (!profileSettings || !profileSettings.profile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <div className="text-center py-8">
          <Brain className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Complete Your Profile
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Add your academic details and interests in Settings to get personalized exam recommendations
          </p>
        </div>
      </motion.div>
    );
  }

  // Analyze academic performance
  const analyzeAcademicPerformance = () => {
    const results = profileSettings.profile.results || {};
    const analysis = {
      overallPerformance: 'average',
      strongSubjects: [],
      weakSubjects: [],
      academicStrength: 0,
      remarks: [],
      class10Score: null,
      class12Score: null
    };

    // Calculate overall academic strength
    let totalPercentage = 0;
    let count = 0;

    if (results.class10?.percentage) {
      analysis.class10Score = parseFloat(results.class10.percentage);
      totalPercentage += analysis.class10Score;
      count++;
    }
    if (results.class12?.percentage) {
      analysis.class12Score = parseFloat(results.class12.percentage);
      totalPercentage += analysis.class12Score;
      count++;
    }
    if (results.graduation?.percentage) {
      totalPercentage += parseFloat(results.graduation.percentage);
      count++;
    }

    if (count > 0) {
      analysis.academicStrength = Math.round(totalPercentage / count);
      
      if (analysis.academicStrength >= 85) {
        analysis.overallPerformance = 'excellent';
        analysis.remarks.push('Outstanding academic record! You have consistently performed at a high level.');
        analysis.remarks.push('Your strong foundation makes you an ideal candidate for top-tier competitive exams.');
      } else if (analysis.academicStrength >= 75) {
        analysis.overallPerformance = 'very good';
        analysis.remarks.push('Strong academic performance with a solid foundation.');
        analysis.remarks.push('You have the potential to excel in competitive examinations with focused preparation.');
      } else if (analysis.academicStrength >= 60) {
        analysis.overallPerformance = 'good';
        analysis.remarks.push('Good academic performance showing consistent effort.');
        analysis.remarks.push('Focus on strengthening weak areas to improve your competitive edge.');
      } else {
        analysis.overallPerformance = 'needs improvement';
        analysis.remarks.push('There is significant room for improvement in your academic performance.');
        analysis.remarks.push('Consistent effort and targeted practice can help you achieve better results.');
      }
    }

    // Analyze Class 12 stream
    if (results.class12?.board) {
      const board = results.class12.board.toLowerCase();
      if (board.includes('science') || board.includes('pcm') || board.includes('pcb')) {
        analysis.strongSubjects.push('Science Stream');
      } else if (board.includes('commerce')) {
        analysis.strongSubjects.push('Commerce Stream');
      } else if (board.includes('arts') || board.includes('humanities')) {
        analysis.strongSubjects.push('Arts/Humanities Stream');
      }
    }

    // Add test performance strengths/weaknesses
    if (cognitiveProfile?.strengths && cognitiveProfile.strengths.length > 0) {
      cognitiveProfile.strengths.slice(0, 3).forEach(strength => {
        analysis.strongSubjects.push(strength.topic);
      });
    }

    if (cognitiveProfile?.weaknesses && cognitiveProfile.weaknesses.length > 0) {
      cognitiveProfile.weaknesses.slice(0, 3).forEach(weakness => {
        analysis.weakSubjects.push(weakness.topic);
      });
    }

    return analysis;
  };

  // Generate exam recommendations
  const generateExamRecommendations = (academicAnalysis) => {
    const recommendations = [];
    const results = profileSettings.profile.results || {};
    const userInterests = interests || [];

    let stream = 'general';
    if (results.class12?.board) {
      const board = results.class12.board.toLowerCase();
      if (board.includes('science') || board.includes('pcm')) {
        stream = 'engineering';
      } else if (board.includes('pcb') || board.includes('biology')) {
        stream = 'medical';
      } else if (board.includes('commerce')) {
        stream = 'commerce';
      } else if (board.includes('arts') || board.includes('humanities')) {
        stream = 'arts';
      }
    }

    // Engineering recommendations
    if (stream === 'engineering' || userInterests.some(i => ['engineering', 'technology', 'computer', 'mathematics', 'coding'].some(keyword => i.toLowerCase().includes(keyword)))) {
      recommendations.push({
        exam: 'JEE Main & Advanced',
        suitability: academicAnalysis.academicStrength >= 75 ? 'Highly Suitable' : 'Suitable',
        reason: 'Premier engineering entrance exam for IITs, NITs, and top engineering colleges.',
        difficulty: 'High',
        careers: ['Engineering', 'Technology', 'Research', 'Innovation']
      });
      recommendations.push({
        exam: 'BITSAT / VITEEE',
        suitability: 'Suitable',
        reason: 'Alternative engineering entrance exams for premier private institutions.',
        difficulty: 'Medium-High',
        careers: ['Engineering', 'Computer Science', 'Technology']
      });
    }

    // Medical recommendations
    if (stream === 'medical' || userInterests.some(i => ['medical', 'biology', 'doctor', 'healthcare', 'medicine'].some(keyword => i.toLowerCase().includes(keyword)))) {
      recommendations.push({
        exam: 'NEET UG',
        suitability: academicAnalysis.academicStrength >= 75 ? 'Highly Suitable' : 'Suitable',
        reason: 'National medical entrance exam for MBBS, BDS, and other medical courses.',
        difficulty: 'High',
        careers: ['Medicine', 'Dentistry', 'Veterinary', 'Pharmacy', 'Healthcare']
      });
    }

    // Commerce recommendations
    if (stream === 'commerce' || userInterests.some(i => ['business', 'finance', 'accounting', 'commerce', 'economics'].some(keyword => i.toLowerCase().includes(keyword)))) {
      recommendations.push({
        exam: 'CA Foundation',
        suitability: 'Highly Suitable',
        reason: 'Chartered Accountancy - prestigious career in finance and accounting.',
        difficulty: 'High',
        careers: ['Chartered Accountant', 'Finance', 'Taxation', 'Audit']
      });
      recommendations.push({
        exam: 'CS Foundation',
        suitability: 'Suitable',
        reason: 'Company Secretary - excellent career in corporate governance and law.',
        difficulty: 'Medium-High',
        careers: ['Company Secretary', 'Corporate Law', 'Compliance']
      });
    }

    // Civil Services
    if (academicAnalysis.academicStrength >= 60 || userInterests.some(i => ['civil services', 'ias', 'ips', 'government', 'administration'].some(keyword => i.toLowerCase().includes(keyword)))) {
      recommendations.push({
        exam: 'UPSC CSE',
        suitability: academicAnalysis.academicStrength >= 75 ? 'Highly Suitable' : 'Suitable',
        reason: 'Most prestigious exam for civil services - IAS, IPS, IFS positions.',
        difficulty: 'Very High',
        careers: ['IAS', 'IPS', 'IFS', 'Civil Services', 'Administration']
      });
    }

    // Banking & SSC
    if (academicAnalysis.academicStrength >= 55) {
      recommendations.push({
        exam: 'IBPS PO / SBI PO',
        suitability: 'Suitable',
        reason: 'Banking sector offers stable career with good growth prospects.',
        difficulty: 'Medium',
        careers: ['Banking', 'Finance', 'Management']
      });
      recommendations.push({
        exam: 'SSC CGL / CHSL',
        suitability: 'Suitable',
        reason: 'Various government job opportunities across departments.',
        difficulty: 'Medium',
        careers: ['Government Jobs', 'Administrative Services']
      });
    }

    // Law
    if (stream === 'arts' || userInterests.some(i => ['law', 'legal', 'advocate', 'judiciary'].some(keyword => i.toLowerCase().includes(keyword)))) {
      recommendations.push({
        exam: 'CLAT / AILET',
        suitability: 'Suitable',
        reason: 'Law entrance exams for National Law Universities.',
        difficulty: 'Medium-High',
        careers: ['Law', 'Judiciary', 'Legal Services', 'Corporate Law']
      });
    }

    return recommendations.slice(0, 6); // Limit to top 6 recommendations
  };

  const academicAnalysis = analyzeAcademicPerformance();
  const examRecommendations = generateExamRecommendations(academicAnalysis);

  const addInterest = () => {
    if (newInterest.trim() && !interests.includes(newInterest.trim())) {
      setInterests([...interests, newInterest.trim()]);
      setNewInterest('');
      setShowInterestInput(false);
    }
  };

  const removeInterest = (index) => {
    setInterests(interests.filter((_, i) => i !== index));
  };

  const getPerformanceColor = (performance) => {
    switch (performance) {
      case 'excellent': return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300';
      case 'very good': return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300';
      case 'good': return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300';
      default: return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300';
    }
  };

  const getSuitabilityColor = (suitability) => {
    if (suitability === 'Highly Suitable') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Very High': return 'text-red-600 dark:text-red-400';
      case 'High': return 'text-orange-600 dark:text-orange-400';
      case 'Medium-High': return 'text-yellow-600 dark:text-yellow-400';
      case 'Medium': return 'text-blue-600 dark:text-blue-400';
      default: return 'text-green-600 dark:text-green-400';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
              <Brain className="h-8 w-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Academic Profile Analysis</h2>
              <p className="text-indigo-100 text-sm">
                Comprehensive analysis of your academic strengths and career recommendations
              </p>
            </div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold">{academicAnalysis.academicStrength}%</div>
            <div className="text-sm text-indigo-100">Academic Strength</div>
          </div>
        </div>
      </div>

      {/* Academic Performance Summary */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <Award className="h-6 w-6 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Academic Performance Summary
          </h3>
        </div>

        <div className={`p-4 rounded-lg border-2 mb-4 ${getPerformanceColor(academicAnalysis.overallPerformance)}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold uppercase">{academicAnalysis.overallPerformance}</span>
            <span className="text-2xl font-bold">{academicAnalysis.academicStrength}%</span>
          </div>
          <div className="space-y-2 text-sm">
            {academicAnalysis.remarks.map((remark, index) => (
              <p key={index}>• {remark}</p>
            ))}
          </div>
        </div>

        {/* Class Scores */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {academicAnalysis.class10Score && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Class 10</p>
              <p className="text-2xl font-bold text-blue-600">{academicAnalysis.class10Score}%</p>
            </div>
          )}
          {academicAnalysis.class12Score && (
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Class 12</p>
              <p className="text-2xl font-bold text-purple-600">{academicAnalysis.class12Score}%</p>
            </div>
          )}
        </div>

        {/* Strengths & Weaknesses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {academicAnalysis.strongSubjects.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-600" />
                Strong Areas
              </h4>
              <div className="space-y-2">
                {academicAnalysis.strongSubjects.map((subject, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded">
                    <span className="text-green-600">✓</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{subject}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {academicAnalysis.weakSubjects.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                <Target className="h-4 w-4 text-orange-600" />
                Areas to Improve
              </h4>
              <div className="space-y-2">
                {academicAnalysis.weakSubjects.map((subject, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                    <span className="text-orange-600">!</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{subject}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Upload Marksheets Prompt */}
      {(!pdfAnalysis || !pdfAnalysis.hasDocuments) && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl shadow-lg p-6 border-2 border-dashed border-amber-300 dark:border-amber-700"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <FileText className="h-6 w-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Upload Your Marksheets for Detailed Analysis
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Upload your Class 10 and Class 12 marksheets in Settings to get:
              </p>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400 mb-4">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Comprehensive academic performance analysis
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Subject-wise strength and weakness identification
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Personalized career and exam recommendations
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Progress tracking from Class 10 to Class 12
                </li>
              </ul>
              <a
                href="/dashboard/settings"
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
              >
                <FileText className="h-4 w-4" />
                Go to Settings
              </a>
            </div>
          </div>
        </motion.div>
      )}

      {/* Interests Section */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Lightbulb className="h-6 w-6 text-yellow-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Your Interests
            </h3>
          </div>
          <button
            onClick={() => setShowInterestInput(!showInterestInput)}
            className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {showInterestInput && (
          <div className="mb-4 flex gap-2">
            <input
              type="text"
              value={newInterest}
              onChange={(e) => setNewInterest(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addInterest()}
              placeholder="Enter your interest (e.g., Class 10, Engineering, Medicine)"
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={addInterest}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Add
            </button>
          </div>
        )}

        {interests.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {interests.map((interest, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-full"
              >
                <span className="text-sm text-gray-700 dark:text-gray-300">{interest}</span>
                <button
                  onClick={() => removeInterest(index)}
                  className="text-gray-500 hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </motion.div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Add your interests to get personalized exam recommendations
          </p>
        )}

        {/* School Suggestions */}
        {userClass && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <School className="h-5 w-5 text-indigo-600" />
                <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                  Best Schools for Class {userClass}
                </h4>
              </div>
              {suggestedSchools.length > 0 && (
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full font-medium">
                  Top Rated
                </span>
              )}
            </div>

            {profileSettings?.profile?.personalInfo?.address && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Showing schools near your location, ranked by quality score
              </p>
            )}

            {loadingSchools ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Finding best schools for you...</p>
              </div>
            ) : suggestedSchools.length > 0 ? (
              <div className="space-y-3">
                {suggestedSchools.map((school, index) => (
                  <motion.div
                    key={school.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`p-3 rounded-lg border hover:shadow-md transition-all ${
                      index === 0 
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-300 dark:border-green-700'
                        : index === 1
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-300 dark:border-blue-700'
                        : 'bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-indigo-200 dark:border-indigo-800'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {index < 3 && (
                            <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-yellow-400 text-yellow-900' :
                              index === 1 ? 'bg-gray-300 text-gray-700' :
                              'bg-orange-400 text-orange-900'
                            }`}>
                              {index + 1}
                            </span>
                          )}
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {school.name}
                          </h5>
                        </div>
                        
                        {school.address && (
                          <div className="flex items-center gap-1 mb-1">
                            <MapPin className="h-3 w-3 text-gray-500 flex-shrink-0" />
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {school.address.city || school.address.state || 'Location not specified'}
                            </p>
                          </div>
                        )}

                        {/* Score Display */}
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex items-center gap-1">
                            <Award className="h-3 w-3 text-indigo-600" />
                            <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                              Score: {school.scoreBreakdown.total}/100
                            </span>
                          </div>
                          {school.established && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              • Est. {school.established}
                            </span>
                          )}
                        </div>

                        {/* Score Breakdown - Show for top 3 */}
                        {index < 3 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {school.scoreBreakdown.experience > 0 && (
                              <span className="text-xs px-1.5 py-0.5 bg-white dark:bg-gray-800 rounded text-gray-600 dark:text-gray-400">
                                Exp: {school.scoreBreakdown.experience}
                              </span>
                            )}
                            {school.scoreBreakdown.faculty > 0 && (
                              <span className="text-xs px-1.5 py-0.5 bg-white dark:bg-gray-800 rounded text-gray-600 dark:text-gray-400">
                                Faculty: {school.scoreBreakdown.faculty}
                              </span>
                            )}
                            {school.scoreBreakdown.results > 0 && (
                              <span className="text-xs px-1.5 py-0.5 bg-white dark:bg-gray-800 rounded text-gray-600 dark:text-gray-400">
                                Results: {school.scoreBreakdown.results}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <a
                        href={`/admissions/${school.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        title="View Details"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <School className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No schools found in our database yet
                </p>
              </div>
            )}

            <div className="mt-4">
              <a
                href="/admissions?type=school"
                className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 font-medium"
              >
                <span>View all schools</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        )}
      </motion.div>

      {/* PDF Analysis Section */}
      {pdfAnalysis && pdfAnalysis.hasDocuments && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-6 w-6 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Marksheet Analysis
            </h3>
          </div>

          {loadingPdfAnalysis ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">Analyzing your marksheets...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Class 10 & 12 Analysis Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pdfAnalysis.class10Analysis && (
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-gray-900 dark:text-white">Class 10</h4>
                      <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-bold">
                        {pdfAnalysis.class10Analysis.grade}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Percentage:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {pdfAnalysis.class10Analysis.percentage}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Board:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {pdfAnalysis.class10Analysis.board}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Year:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {pdfAnalysis.class10Analysis.year}
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          pdfAnalysis.class10Analysis.performanceLevel === 'Outstanding' || 
                          pdfAnalysis.class10Analysis.performanceLevel === 'Excellent' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : pdfAnalysis.class10Analysis.performanceLevel === 'Good'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {pdfAnalysis.class10Analysis.performanceLevel}
                        </span>
                      </div>
                      {pdfAnalysis.class10Analysis.documentUrl && (
                        <a
                          href={pdfAnalysis.class10Analysis.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 mt-3 text-blue-600 hover:text-blue-700 dark:text-blue-400"
                        >
                          <Download className="h-4 w-4" />
                          <span className="text-xs">View Document</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {pdfAnalysis.class12Analysis && (
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-gray-900 dark:text-white">Class 12</h4>
                      <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-sm font-bold">
                        {pdfAnalysis.class12Analysis.grade}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Percentage:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {pdfAnalysis.class12Analysis.percentage}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Stream:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {pdfAnalysis.class12Analysis.board}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Year:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {pdfAnalysis.class12Analysis.year}
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-800">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          pdfAnalysis.class12Analysis.performanceLevel === 'Outstanding' || 
                          pdfAnalysis.class12Analysis.performanceLevel === 'Excellent' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : pdfAnalysis.class12Analysis.performanceLevel === 'Good'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {pdfAnalysis.class12Analysis.performanceLevel}
                        </span>
                      </div>
                      {pdfAnalysis.class12Analysis.documentUrl && (
                        <a
                          href={pdfAnalysis.class12Analysis.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 mt-3 text-purple-600 hover:text-purple-700 dark:text-purple-400"
                        >
                          <Download className="h-4 w-4" />
                          <span className="text-xs">View Document</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Insights from PDF Analysis */}
              {pdfAnalysis.insights && pdfAnalysis.insights.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Key Insights
                  </h4>
                  {pdfAnalysis.insights.map((insight, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        insight.type === 'positive'
                          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                          : insight.type === 'warning'
                          ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
                          : insight.type === 'info'
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                          : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      }`}
                    >
                      {insight.type === 'positive' && <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />}
                      {insight.type === 'warning' && <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />}
                      {insight.type === 'info' && <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                      {insight.type === 'neutral' && <Info className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />}
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                          {insight.title}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {insight.message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Recommendations from PDF Analysis */}
              {pdfAnalysis.recommendations && pdfAnalysis.recommendations.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Personalized Recommendations
                  </h4>
                  {pdfAnalysis.recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border ${
                        rec.priority === 'critical'
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                          : rec.priority === 'high'
                          ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
                          : rec.priority === 'medium'
                          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {rec.title}
                        </p>
                        <span className="text-xs px-2 py-0.5 bg-white dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-400">
                          {rec.category}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {rec.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Exam Recommendations */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <FileCheck className="h-6 w-6 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Recommended Exams for You
          </h3>
        </div>

        <div className="space-y-4">
          {examRecommendations.map((rec, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-blue-900/20 rounded-lg border border-gray-200 dark:border-gray-600"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white">{rec.exam}</h4>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getSuitabilityColor(rec.suitability)}`}>
                    {rec.suitability}
                  </span>
                </div>
                <span className={`text-sm font-semibold ${getDifficultyColor(rec.difficulty)}`}>
                  {rec.difficulty}
                </span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{rec.reason}</p>
              <div className="flex flex-wrap gap-2">
                {rec.careers.map((career, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs text-gray-700 dark:text-gray-300"
                  >
                    {career}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {examRecommendations.length === 0 && (
          <p className="text-center text-gray-600 dark:text-gray-400 py-8">
            Add your interests and complete your academic profile to get personalized exam recommendations
          </p>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ProfileIntegrationPanel;
